import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import { 
  TrendingUp, Users, Receipt, IndianRupee, Clipboard, Gift, 
  MapPin, Clock, Award, ShieldAlert, CheckCircle, RefreshCw 
} from "lucide-react";
import { employeeWorkforceAPI } from "../../api/apiService";
import { NotificationContext } from "../../context/NotificationContext";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";
import EmptyState from "../../components/EmptyState";

export default function SalesExecutiveDashboard() {
  const { user } = useAuth();
  const { addNotification } = useContext(NotificationContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const res = await employeeWorkforceAPI.getDashboard();
      setData(res.data);
    } catch (err) {
      console.error(err);
      addNotification("Failed to load dashboard data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <EmptyState
          preset="verification"
          title="Loading your Sales Dashboard..."
          message="Please wait a moment while we aggregate performance stats."
        />
      </div>
    );
  }

  const attendanceColors = {
    "ON TIME": "bg-green-50 text-green-600 dark:bg-green-950/30 dark:text-green-400 border border-green-200 dark:border-green-800",
    "LATE": "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800",
    "FIELD VISIT": "bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800",
    "HALF DAY": "bg-orange-50 text-orange-600 dark:bg-orange-950/30 dark:text-orange-400 border border-orange-200 dark:border-orange-800",
    "ABSENT": "bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400 border border-red-200 dark:border-red-800",
    "NOT CHECKED IN": "bg-gray-100 text-gray-500 dark:bg-slate-800 dark:text-slate-400 border border-gray-200 dark:border-slate-700"
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Greeting */}
        <div className="bg-white dark:bg-slate-800 p-6 md:p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-green-500/20">
                {user?.first_name?.[0] || "S"}{user?.last_name?.[0] || "E"}
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">
                  Welcome, {user?.first_name || "Sales Executive"}!
                </h1>
                <p className="text-gray-500 dark:text-slate-400 font-medium tracking-wide">
                  Salesman ID: <span className="font-bold text-green-600 dark:text-green-400">{user?.salesman_id || "SE-Pending"}</span>
                </p>
              </div>
            </div>
            
            {/* Quick Stats Summary */}
            <div className="flex items-center gap-3">
              <span className={`px-4 py-2 rounded-2xl text-xs font-black uppercase tracking-wider ${attendanceColors[data?.attendance_today || "NOT CHECKED IN"]}`}>
                ● {data?.attendance_today}
              </span>
              {data?.attendance_today === "NOT CHECKED IN" && (
                <Link
                  to="/sales-executive/attendance"
                  className="px-5 py-2 bg-green-600 text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-green-700 transition active:scale-95 shadow-md shadow-green-500/10"
                >
                  Check In Now
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Top level Dashboard statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard 
            label="Revenue generated (Month)" 
            value={`₹${data?.revenue_month?.toLocaleString() || '0'}`} 
            sub={`Today: ₹${data?.revenue_today?.toLocaleString() || '0'}`} 
            icon={IndianRupee} 
            color="emerald" 
          />
          <StatCard 
            label="Incentives Estimated" 
            value={`₹${data?.incentives_earned?.toLocaleString() || '0'}`} 
            sub={`${data?.incentive_percentage || 0}% slab percentage`} 
            icon={Gift} 
            color="purple" 
          />
          <StatCard 
            label="Total Invoices Created" 
            value={data?.bills_month || 0} 
            sub={`Today: ${data?.bills_today || 0}`} 
            icon={Receipt} 
            color="blue" 
          />
          <StatCard 
            label="Pending Collections" 
            value={`₹${data?.pending_collections?.toLocaleString() || '0'}`} 
            sub={`Verified: ₹${data?.verified_collections_month?.toLocaleString() || '0'}`} 
            icon={Clipboard} 
            color="orange" 
          />
        </div>

        {/* Target Achievements & Attendance Status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Target Progress Meter */}
          <div className="lg:col-span-2 bg-white dark:bg-slate-800 p-6 md:p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider">
                  Monthly Sales Target
                </h2>
                <span className="text-xs font-black bg-green-50 text-green-600 dark:bg-green-950/30 dark:text-green-400 px-3 py-1 rounded-full uppercase">
                  {data?.target_achievement_pct?.toFixed(0)}% Achieved
                </span>
              </div>
              <p className="text-gray-500 dark:text-slate-400 text-sm font-medium mb-6">
                Monthly Target volume is set to <span className="font-bold text-gray-800 dark:text-white">₹{data?.monthly_target?.toLocaleString()}</span>. 
                Complete more sales to unlock higher commission rates!
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="w-full bg-gray-100 dark:bg-slate-900 rounded-full h-4 overflow-hidden relative border border-gray-200/50 dark:border-slate-800">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${data?.target_achievement_pct || 0}%` }}
                  transition={{ duration: 1 }}
                  className="h-full bg-gradient-to-r from-emerald-500 to-green-600 rounded-full"
                />
              </div>
              <div className="flex justify-between text-xs font-black text-gray-400 uppercase tracking-widest">
                <span>₹0</span>
                <span>₹{data?.revenue_month?.toLocaleString()} Current</span>
                <span>Target: ₹{data?.monthly_target?.toLocaleString()}</span>
              </div>
            </div>

            {/* Slabs guide */}
            <div className="mt-8 border-t border-gray-100 dark:border-slate-700 pt-6">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Incentive Commission Slabs:</p>
              <div className="grid grid-cols-3 gap-2">
                <SlabInfo value="₹50k+" pct="2%" active={data?.revenue_month >= 50000} />
                <SlabInfo value="₹100k+" pct="5%" active={data?.revenue_month >= 100000} />
                <SlabInfo value="₹500k+" pct="10%" active={data?.revenue_month >= 500000} />
              </div>
            </div>
          </div>

          {/* Today Attendance Card */}
          <div className="bg-white dark:bg-slate-800 p-6 md:p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700 flex flex-col justify-between">
            <div>
              <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider mb-6">
                Today's Attendance
              </h2>
              
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-green-50 dark:bg-green-950/20 text-green-600 dark:text-green-400 rounded-2xl">
                  <Clock className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Punched Status</p>
                  <p className="text-lg font-black text-gray-900 dark:text-white">{data?.attendance_today}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between text-sm py-2 border-b border-gray-50 dark:border-slate-700">
                  <span className="text-gray-400 font-medium">Punch In Time</span>
                  <span className="font-bold text-gray-800 dark:text-white">
                    {data?.punch_in_time ? new Date(data.punch_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between text-sm py-2 border-b border-gray-50 dark:border-slate-700">
                  <span className="text-gray-400 font-medium">Punch Out Time</span>
                  <span className="font-bold text-gray-800 dark:text-white">
                    {data?.punch_out_time ? new Date(data.punch_out_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between text-sm py-2">
                  <span className="text-gray-400 font-medium">New Customers Today</span>
                  <span className="font-bold text-green-600 dark:text-green-400">
                    +{data?.new_customers_today || 0}
                  </span>
                </div>
              </div>
            </div>

            <Link
              to="/sales-executive/attendance"
              className="mt-6 w-full flex items-center justify-center gap-2 py-3 bg-gray-50 dark:bg-slate-700 text-gray-800 dark:text-white border border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-600/80 rounded-2xl font-bold transition active:scale-95"
            >
              <MapPin className="w-4 h-4 text-green-500" />
              Check-in / Check-out Panel
            </Link>
          </div>

        </div>

      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color }) {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400",
    purple: "bg-purple-50 text-purple-600 dark:bg-purple-950/20 dark:text-purple-400",
    blue: "bg-blue-50 text-blue-600 dark:bg-blue-950/20 dark:text-blue-400",
    orange: "bg-orange-50 text-orange-600 dark:bg-orange-950/20 dark:text-orange-400",
  };

  return (
    <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm flex items-center gap-4 hover:shadow-md transition-shadow">
      <div className={`p-4 rounded-2xl ${colors[color]}`}>
        <Icon className="w-7 h-7" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest truncate">{label}</p>
        <p className="text-xl md:text-2xl font-black text-gray-900 dark:text-white mt-1 truncate">{value}</p>
        <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold mt-1 truncate">{sub}</p>
      </div>
    </div>
  );
}

function SlabInfo({ value, pct, active }) {
  return (
    <div className={`p-3 rounded-xl border text-center transition-all ${
      active 
        ? "bg-green-50 border-green-200 text-green-600 dark:bg-green-950/30 dark:border-green-800 dark:text-green-400 font-bold shadow-sm" 
        : "bg-gray-50/50 border-gray-100 text-gray-400 dark:bg-slate-800/40 dark:border-slate-700 font-medium"
    }`}>
      <p className="text-[10px] uppercase font-bold tracking-wider">Volume</p>
      <p className="text-sm font-black mt-0.5">{value}</p>
      <p className="text-xs mt-1 font-bold">{pct} Comm.</p>
    </div>
  );
}
